import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { IMAGES } from "../../lib/content";

const lines = ["Group blocks,", "booked with", "peace of mind."];

const lineParent = {
  hidden: {},
  show: { transition: { staggerChildren: 0.14, delayChildren: 0.35 } },
};
const lineChild = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 1, ease: [0.76, 0, 0.24, 1] } },
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  const scrollToServices = () =>
    document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} data-testid="hero" className="relative h-[100svh] min-h-[640px] overflow-hidden">
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="Luxury resort at sunset" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70" />
      </motion.div>

      <motion.div
        style={{ y: textY }}
        className="relative z-10 h-full mx-auto max-w-[1400px] px-6 md:px-10 flex flex-col justify-end pb-16 md:pb-24"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="label-eyebrow text-white/80 mb-6 flex items-center gap-3"
        >
          <span className="h-px w-10 bg-primary inline-block" />
          Hotel & Venue Group Blocks
        </motion.div>

        <motion.h1
          variants={lineParent}
          initial="hidden"
          animate="show"
          className="font-display text-white font-light tracking-tight leading-[0.92] text-[3.4rem] sm:text-7xl md:text-8xl lg:text-[8.5rem]"
        >
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span variants={lineChild} className="block">
                {line}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1 }}
          className="mt-8 flex flex-col md:flex-row md:items-end justify-between gap-8"
        >
          <p className="text-white/85 text-base md:text-lg max-w-xl leading-relaxed font-light">
            Rest Assured Group Travel LLC secures hotel and venue room blocks for corporate teams,
            families, sports groups, and congregations — so everyone stays together, at the right rate.
          </p>
          <button
            data-testid="hero-cta"
            onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
            className="shrink-0 bg-primary text-primary-foreground px-9 py-4 label-eyebrow hover:bg-[#a04d2d] transition-colors"
          >
            Request Services
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        onClick={scrollToServices}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 right-6 md:right-10 z-10 text-white/70 hover:text-white"
        aria-label="Scroll to services"
      >
        <ArrowDown className="w-5 h-5 animate-bounce" />
      </motion.button>
    </section>
  );
};
